/**
 * MenuBoard — customer-facing TV display for 1920×1080 Yodeck players.
 *
 * Theme: "Neo-Kawaii" — premium + super-vibrant. Animated aurora background,
 * frosted "candy glass" panels with colored glows + a sweeping sheen, chunky
 * 3D gradient step badges, a neon-glow "Drink of the Day" hero, glossy dancing
 * mascot cups and glowing/twinkling confetti. Reads from across the room.
 *
 * Layout:
 *   [Header: logo + wordmark + dancing cup + hours]
 *   [Pearl timer ribbon — only when active]
 *   [Featured "Drink of the Day" hero panel — only when enabled]
 *   [4-step grid: Size → Base → Flavor → Pearls]
 *   [Footer with mascot cups + tagline]
 */
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useMenuBoardState } from "@/lib/queries";
import { LOGO_URL } from "@/components/BobaLogo";
import KawaiiBobaCup from "@/components/KawaiiBobaCup";
import KawaiiConfetti from "@/components/KawaiiConfetti";
import { SocialFollowBar } from "@/components/SocialFollowCTA";
import { Sparkles, HeartCrack, ReceiptText } from "lucide-react";

const formatPrice = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const findGroup = (groups, regex) => groups.find((g) => regex.test(g.name || ""));

// Per-step accent: 3D badge gradient, glow color (for the card shadow) and
// the pastel option-pill gradient. Keeps the rainbow variety while every
// badge shares the vibrant accent→primary family.
const ACCENTS = {
  violet: { badge: "linear-gradient(135deg, hsl(330 82% 62%), hsl(252 66% 56%))", glow: "hsla(252,63%,54%,0.4)",  pill: "from-violet-100/90 to-fuchsia-100/90" },
  pink:   { badge: "linear-gradient(135deg, hsl(45 96% 60%), hsl(330 82% 62%))",  glow: "hsla(330,78%,60%,0.4)",  pill: "from-pink-100/90 to-rose-100/90" },
  peach:  { badge: "linear-gradient(135deg, hsl(45 96% 60%), hsl(20 92% 62%))",   glow: "hsla(28,95%,58%,0.4)",   pill: "from-amber-100/90 to-orange-100/90" },
  plum:   { badge: "linear-gradient(135deg, hsl(288 72% 60%), hsl(330 82% 62%))", glow: "hsla(290,62%,56%,0.4)",  pill: "from-fuchsia-100/90 to-purple-100/90" },
  teal:   { badge: "linear-gradient(135deg, hsl(170 72% 46%), hsl(190 82% 56%))", glow: "hsla(176,70%,46%,0.4)",  pill: "from-teal-100/90 to-cyan-100/90" },
};

/* ---------- Sold-out "back soon" kawaii pill ---------- */
function BackSoonPill({ testid }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full bg-white border-2 border-pink-300 text-pink-600 font-display font-extrabold text-xs tracking-[0.16em] uppercase pl-2 pr-3 py-1 shadow-[0_4px_14px_hsla(330,78%,60%,0.45)] rotate-[3deg]"
      data-testid={testid}
      title="Sold out — back soon!"
    >
      <HeartCrack className="w-3.5 h-3.5 text-pink-500" aria-hidden="true" />
      Back soon
    </span>
  );
}

/* ---------- Pearl timer ribbon ---------- */
function PearlTimerRibbon({ timer, compact = false }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const readyAt = new Date(timer.ready_at).getTime();
  const remainingMs = Math.max(0, readyAt - now);
  const remMin = Math.floor(remainingMs / 60_000);
  const remSec = Math.floor((remainingMs % 60_000) / 1000);
  const isReady = remainingMs <= 0;
  return (
    <section
      data-testid="menu-board-pearl-timer"
      className={[
        "relative overflow-hidden rounded-[32px] border-4 border-white",
        compact ? "px-5 py-4" : "px-8 py-6",
        isReady ? "text-emerald-950 bp-neon-pulse" : "text-white bp-neon-pulse",
      ].join(" ")}
      style={{
        background: isReady
          ? "linear-gradient(135deg, hsl(150 70% 82%), hsl(160 65% 72%))"
          : "linear-gradient(135deg, hsl(38 95% 55%) 0%, hsl(20 92% 58%) 100%)",
      }}
    >
      {!isReady && <span aria-hidden="true" className="bp-barber absolute inset-0 opacity-70" />}
      <div className={`relative ${compact ? "flex items-center gap-3" : "flex items-center gap-5"}`}>
        <KawaiiBobaCup variant={isReady ? "matcha" : "lilac"} className={compact ? "w-14 h-20 bp-dance bp-mascot shrink-0" : "w-20 h-24 bp-dance bp-mascot"} />
        <div className="min-w-0 flex-1">
          <p className="font-display text-xs uppercase tracking-[0.32em] font-black opacity-90">
            {timer.notes || "Tapioca pearls"}
          </p>
          <h2 className={compact
            ? "font-display text-2xl font-black leading-tight mt-0.5"
            : "font-display text-4xl md:text-5xl font-black leading-tight mt-0.5"}>
            {isReady ? "Fresh batch ready! 🎉" : compact ? "Pearls cooking!" : "Pearls cooking — get ready!"}
          </h2>
          {!isReady && (
            <p className={compact
              ? "font-display text-2xl font-extrabold mt-0.5 tabular-nums"
              : "font-display text-3xl font-extrabold mt-1 tabular-nums"}>
              {String(remMin).padStart(2, "0")}:{String(remSec).padStart(2, "0")}
              <span className="text-sm opacity-90 ml-2 font-bold uppercase tracking-wide">until next batch</span>
            </p>
          )}
        </div>
        {!compact && <KawaiiBobaCup variant="pink" className="w-20 h-24 bp-dance bp-mascot hidden md:block" flip />}
      </div>
    </section>
  );
}

/* ---------- Drink-of-the-day hero panel (neon glow) ---------- */
function FeaturedDrinkHero({ featured, compact = false }) {
  const item = featured.menu_item;
  const Badge = featured.badge_text ? (
    <span
      className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full font-display text-sm font-black uppercase tracking-[0.2em] text-white shadow-lg"
      style={{ background: "linear-gradient(90deg, hsl(45 96% 58%), hsl(330 82% 60%))" }}
      data-testid="menu-board-featured-badge"
    >
      <Sparkles className="w-4 h-4" />
      {featured.badge_text}
    </span>
  ) : null;

  const panelStyle = {
    background:
      "radial-gradient(120% 90% at 15% 10%, hsl(290 60% 40%) 0%, transparent 55%)," +
      "linear-gradient(135deg, hsl(252 66% 30%) 0%, hsl(272 58% 34%) 55%, hsl(330 58% 40%) 100%)",
  };

  const Price = item ? (
    <span
      className="font-display text-3xl font-black text-amber-300 tabular-nums bp-candy-pop drop-shadow-[0_2px_6px_rgba(0,0,0,0.35)]"
      data-testid="menu-board-featured-price"
    >
      {formatPrice(item.base_price_cents)}
    </span>
  ) : null;

  if (compact) {
    return (
      <section
        data-testid="menu-board-featured"
        className="relative overflow-hidden rounded-[36px] p-6 border-4 border-white/25 text-white bp-neon-pulse bp-glass-shine"
        style={panelStyle}
      >
        <div className="relative flex flex-col items-center text-center gap-2.5">
          <div className="relative">
            <KawaiiBobaCup variant="pink" className="w-32 h-40 bp-dance bp-mascot" />
            <span aria-hidden="true" className="absolute -top-1 -right-2 text-3xl bp-bob">⭐</span>
            <span aria-hidden="true" className="absolute -bottom-1 -left-2 text-2xl bp-bob" style={{ animationDelay: "0.5s" }}>💖</span>
          </div>
          {Badge}
          <h2 className="font-display text-4xl font-black leading-[0.95]" data-testid="menu-board-featured-headline">
            <span className="bg-gradient-to-r from-amber-200 via-pink-200 to-white bg-clip-text text-transparent">
              {featured.headline || item?.name || "Today's special"}
            </span>
          </h2>
          {featured.subtext && (
            <p className="font-display text-lg text-white/80 font-semibold leading-snug">{featured.subtext}</p>
          )}
          {item && (
            <div className="flex items-baseline gap-2 mt-0.5">
              <span className="font-display text-2xl font-extrabold text-white">{item.name}</span>
              {Price}
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section
      data-testid="menu-board-featured"
      className="relative overflow-hidden rounded-[40px] p-8 border-4 border-white/25 text-white bp-neon-pulse bp-glass-shine"
      style={panelStyle}
    >
      <div className="relative flex items-center gap-8">
        <div className="shrink-0 hidden md:block relative">
          <KawaiiBobaCup variant="pink" className="w-48 h-60 bp-dance bp-mascot" />
          <span aria-hidden="true" className="absolute -top-2 -right-2 text-4xl bp-bob">⭐</span>
          <span aria-hidden="true" className="absolute -bottom-1 -left-3 text-3xl bp-bob" style={{ animationDelay: "0.5s" }}>💖</span>
        </div>
        <div className="min-w-0 flex-1">
          {Badge}
          <h2 className="font-display text-6xl lg:text-7xl font-black leading-[0.95] mt-2" data-testid="menu-board-featured-headline">
            <span className="bg-gradient-to-r from-amber-200 via-pink-200 to-white bg-clip-text text-transparent">
              {featured.headline || item?.name || "Today's special"}
            </span>
          </h2>
          {featured.subtext && (
            <p className="font-display text-2xl text-white/85 font-semibold mt-2 leading-snug">{featured.subtext}</p>
          )}
          {item && (
            <div className="mt-3 flex items-baseline gap-3">
              <span className="font-display text-3xl font-extrabold text-white">{item.name}</span>
              {Price}
            </div>
          )}
        </div>
        <div className="shrink-0 hidden lg:block">
          <KawaiiBobaCup variant="taro" className="w-36 h-44 bp-dance bp-mascot" flip style={{ animationDelay: "0.4s" }} />
        </div>
      </div>
    </section>
  );
}

/* ---------- Step header (shared) ---------- */
function StepHeader({ number, title, accent, rule, size = "md" }) {
  const a = ACCENTS[accent] || ACCENTS.violet;
  return (
    <header className="flex items-center gap-4 mb-3">
      {number != null && (
        <span
          className={`shrink-0 grid place-items-center rounded-full font-display font-black text-white border-4 border-white ${size === "lg" ? "w-16 h-16 text-3xl" : "w-14 h-14 text-2xl"}`}
          style={{ background: a.badge, boxShadow: `0 10px 22px ${a.glow}` }}
        >
          {number}
        </span>
      )}
      <div className="min-w-0 flex-1">
        {number != null && (
          <p className="font-display text-xs uppercase tracking-[0.34em] text-primary/70 font-black">
            Step {number}
          </p>
        )}
        <h2 className="font-display text-3xl lg:text-4xl font-black leading-tight text-foreground">
          {title}
        </h2>
      </div>
      {rule && (
        <span
          data-testid={`menu-board-step-${number}-rule`}
          className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full font-display text-xs font-black uppercase tracking-[0.16em] text-white border-2 border-white"
          style={{ background: a.badge, boxShadow: `0 6px 16px ${a.glow}` }}
        >
          <Sparkles className="w-3.5 h-3.5" />
          {rule}
        </span>
      )}
    </header>
  );
}

/* ---------- Step block ---------- */
function StepBlock({ number, title, group, hint, rule, twoCols = false, accent = "violet" }) {
  const a = ACCENTS[accent] || ACCENTS.violet;
  if (!group) {
    return (
      <section
        className="rounded-[40px] border-4 border-dashed border-primary/25 bg-white/50 p-8 text-primary/60 text-center h-full grid place-items-center"
        data-testid={`menu-board-step-${number}-empty`}
      >
        <p className="font-display text-base uppercase tracking-widest font-bold">
          Step {number} — {title} not configured
        </p>
        <p className="text-sm mt-1 opacity-80">
          Add a "{title}" modifier group from /admin/menu to populate this step.
        </p>
      </section>
    );
  }
  const opts = [...(group.options || [])].sort(
    (x, y) => (x.available === false) - (y.available === false),
  );
  return (
    <section
      className="relative bp-candy-glass bp-glass-shine p-5 flex flex-col"
      style={{ boxShadow: `0 22px 50px -20px ${a.glow}, 0 2px 0 hsla(0,0%,100%,0.7) inset` }}
      data-testid={`menu-board-step-${number}`}
    >
      <StepHeader number={number} title={title} accent={accent} rule={rule} />
      <ul className={twoCols ? "grid grid-cols-2 gap-x-4 gap-y-1.5" : "space-y-1.5"}>
        {opts.map((o) => {
          const out = o.available === false;
          const free = !o.price_delta_cents;
          return (
            <li
              key={o.id}
              data-testid={`menu-board-opt-${o.id}`}
              className={[
                "flex items-baseline justify-between gap-3 px-4 py-2 rounded-2xl border-2",
                out
                  ? "bg-white/70 border-pink-200 opacity-70 -rotate-[1deg]"
                  : `bg-gradient-to-br ${a.pill} border-white shadow-sm`,
              ].join(" ")}
            >
              <span
                className={[
                  "font-display text-2xl lg:text-3xl font-bold leading-tight",
                  out ? "text-pink-900/55" : "text-foreground",
                ].join(" ")}
                style={
                  out
                    ? {
                        textDecoration: "line-through wavy",
                        textDecorationColor: "rgba(236, 72, 153, 0.6)",
                        textDecorationThickness: "2px",
                      }
                    : undefined
                }
              >
                {o.label}
              </span>
              <span className="shrink-0 flex items-baseline gap-2">
                {!out && !free && (
                  <span className="font-display text-xl lg:text-2xl font-black text-accent tabular-nums">
                    +{formatPrice(o.price_delta_cents)}
                  </span>
                )}
                {out && <BackSoonPill testid="menu-board-out-badge" />}
              </span>
            </li>
          );
        })}
      </ul>
      {hint && (
        <p className="mt-3 text-sm text-muted-foreground italic font-semibold">{hint}</p>
      )}
    </section>
  );
}

/* ---------- Category-items block (Boba / Drinks / Snacks) ---------- */
function CategoryBlock({ number, title, items, accent = "violet", emptyMessage }) {
  const a = ACCENTS[accent] || ACCENTS.violet;
  const visible = (items || [])
    .filter((it) => it)
    .sort((x, y) => (x.available === false) - (y.available === false));
  const slug = String(title).toLowerCase().replace(/[^a-z0-9]+/g, "-");

  return (
    <section
      className="relative bp-candy-glass bp-glass-shine p-5 flex flex-col"
      style={{ boxShadow: `0 22px 50px -20px ${a.glow}, 0 2px 0 hsla(0,0%,100%,0.7) inset` }}
      data-testid={`menu-board-category-${slug}`}
    >
      <StepHeader number={number} title={title} accent={accent} size="lg" />
      {visible.length === 0 ? (
        <p className="text-base text-muted-foreground italic" data-testid={`menu-board-category-${slug}-empty`}>
          {emptyMessage || `No ${title} items yet — add some from /admin/menu.`}
        </p>
      ) : (
        <ul className="grid grid-cols-1 gap-2">
          {visible.map((it) => {
            const out = it.available === false;
            return (
              <li
                key={it.id}
                data-testid={`menu-board-item-${it.id}`}
                className={[
                  "flex items-baseline justify-between gap-3 px-4 py-1.5 rounded-2xl border-2",
                  out
                    ? "bg-white/70 border-pink-200 opacity-70 -rotate-[1deg]"
                    : `bg-gradient-to-br ${a.pill} border-white shadow-sm`,
                ].join(" ")}
              >
                <div className="min-w-0 flex-1">
                  <p
                    className={[
                      "font-display text-3xl lg:text-4xl font-bold leading-tight truncate",
                      out ? "text-pink-900/55" : "text-foreground",
                    ].join(" ")}
                    style={
                      out
                        ? {
                            textDecoration: "line-through wavy",
                            textDecorationColor: "rgba(236, 72, 153, 0.6)",
                            textDecorationThickness: "2px",
                          }
                        : undefined
                    }
                  >
                    {it.name}
                  </p>
                  {it.description && (
                    <p className="text-base text-muted-foreground mt-0.5 truncate">{it.description}</p>
                  )}
                </div>
                <span className="shrink-0 flex items-baseline gap-2">
                  {!out && it.base_price_cents > 0 && (
                    <span className="font-display text-2xl lg:text-3xl font-black text-accent tabular-nums">
                      {formatPrice(it.base_price_cents)}
                    </span>
                  )}
                  {out && <BackSoonPill />}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/* ---------- Aurora background ---------- */
function AuroraBackdrop() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="bp-aurora-blob" style={{ width: 760, height: 760, top: -180, left: -140, background: "radial-gradient(circle, hsla(330,92%,72%,0.85), transparent 70%)" }} />
      <div className="bp-aurora-blob" style={{ width: 800, height: 800, top: -140, right: -180, background: "radial-gradient(circle, hsla(252,90%,74%,0.85), transparent 70%)", animationDelay: "-6s" }} />
      <div className="bp-aurora-blob" style={{ width: 680, height: 680, bottom: -200, left: 140, background: "radial-gradient(circle, hsla(45,96%,66%,0.7), transparent 70%)", animationDelay: "-12s" }} />
      <div className="bp-aurora-blob" style={{ width: 740, height: 740, bottom: -180, right: 60, background: "radial-gradient(circle, hsla(175,82%,62%,0.6), transparent 70%)", animationDelay: "-18s" }} />
    </div>
  );
}

/* ---------- Flashy video backdrop (selectable "flashy" theme) ---------- */
function FlashyVideoBg() {
  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-0 overflow-hidden" style={{ zIndex: 0 }}>
      <video
        className="absolute inset-0 w-full h-full object-cover"
        src="/menu-flashy-bg.mp4"
        autoPlay
        muted
        loop
        playsInline
        data-testid="menu-board-flashy-video"
      />
      {/* Legibility scrim: darker at very top & bottom, clearer through the middle */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(10,6,18,0.72) 0%, rgba(10,6,18,0.28) 22%, rgba(10,6,18,0.20) 55%, rgba(10,6,18,0.55) 88%, rgba(10,6,18,0.82) 100%)",
        }}
      />
    </div>
  );
}

/* ---------- Fit-to-box: scale a block's content so ALL of it shows no matter
   how large/small the free-form box is (measured in the 1920×1080 space). ---- */
function FitToBox({ boxW, boxH, ha = "center", va = "center", children }) {
  const ref = useRef(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const natW = el.scrollWidth || 1;
      const natH = el.scrollHeight || 1;
      const s = Math.min(boxW / natW, boxH / natH);
      setScale(Number.isFinite(s) && s > 0 ? Math.min(s, 2.5) : 1);
    };
    measure();
    const ro = "ResizeObserver" in window ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    return () => ro?.disconnect();
  }, [boxW, boxH]);
  const justify = { start: "justify-start", center: "justify-center", end: "justify-end" }[ha] || "justify-center";
  const items = { start: "items-start", center: "items-center", end: "items-end" }[va] || "items-center";
  const originX = { start: "left", center: "center", end: "right" }[ha] || "center";
  const originY = { start: "top", center: "center", end: "bottom" }[va] || "center";
  return (
    <div className={`w-full h-full flex overflow-hidden ${justify} ${items}`}>
      <div ref={ref} style={{ width: boxW, transform: `scale(${scale})`, transformOrigin: `${originX} ${originY}` }}>
        {children}
      </div>
    </div>
  );
}

/* ---------- Live order ticket (customer-facing) ---------- */
function LiveOrderTicket({ order }) {
  const empty = !order || order.empty;
  const t = order?.totals || {};
  const RowLine = ({ label, v }) => (
    <div className="flex justify-between text-foreground/70">
      <span>{label}</span>
      <span className="tabular-nums">{formatPrice(v)}</span>
    </div>
  );
  return (
    <div className="w-full bp-candy-glass rounded-[28px] p-5 bg-white/92 border-2 border-white shadow-xl">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display font-black text-2xl text-primary flex items-center gap-2">
          <ReceiptText className="w-6 h-6" /> Your Order
        </h2>
        {!empty && order.number && <span className="text-sm font-bold text-muted-foreground">#{order.number}</span>}
      </div>
      {empty ? (
        <p className="text-foreground/60 font-display text-lg py-8 text-center">Your order will appear here 🧋</p>
      ) : (
        <>
          <div className="space-y-2">
            {order.lines.map((ln) => (
              <div key={ln.id} className="flex justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-bold text-foreground leading-tight">
                    <span className="text-primary">{ln.qty}×</span> {ln.name}
                  </p>
                  {ln.modifiers?.length > 0 && (
                    <p className="text-xs text-foreground/60 leading-tight">{ln.modifiers.join(" · ")}</p>
                  )}
                </div>
                <span className="font-bold tabular-nums text-foreground shrink-0">{formatPrice(ln.line_total_cents)}</span>
              </div>
            ))}
          </div>
          <div className="border-t-2 border-primary/10 mt-3 pt-2 text-sm space-y-1">
            <RowLine label="Subtotal" v={t.subtotal || 0} />
            {t.discount > 0 && <RowLine label="Discount" v={-t.discount} />}
            {t.tax > 0 && <RowLine label="Tax" v={t.tax} />}
            {t.surcharge > 0 && <RowLine label="Card fee" v={t.surcharge} />}
          </div>
          <div className="flex justify-between items-center mt-2">
            <span className="font-display font-black text-xl">Total</span>
            <span className="font-display font-black text-2xl text-primary tabular-nums">{formatPrice(t.total || 0)}</span>
          </div>
        </>
      )}
    </div>
  );
}

/* ---------- Page ---------- */
export default function MenuBoard() {
  const { data, isError } = useMenuBoardState({ refetchInterval: 15_000 });
  const [searchParams] = useSearchParams();
  const style = searchParams.get("style") || data?.layout?.style || "classic";
  const flashy = style === "flashy";
  const editMode = searchParams.get("edit") === "1";

  const boardRef = useRef(null);
  const [fit, setFit] = useState({ sx: 1, sy: 1 });
  useEffect(() => {
    const recompute = () => {
      const el = boardRef.current;
      const h = el ? Math.max(el.offsetHeight, el.scrollHeight, 1080) : 1080;
      setFit({ sx: window.innerWidth / 1920, sy: window.innerHeight / h });
    };
    recompute();
    const ro = "ResizeObserver" in window ? new ResizeObserver(recompute) : null;
    if (ro && boardRef.current) ro.observe(boardRef.current);
    window.addEventListener("resize", recompute);
    window.addEventListener("orientationchange", recompute);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", recompute);
      window.removeEventListener("orientationchange", recompute);
    };
  }, [data]);

  useEffect(() => {
    document.body.classList.add("menu-board-active");
    document.documentElement.style.background = "#fdf2fb";
    return () => {
      document.body.classList.remove("menu-board-active");
      document.documentElement.style.background = "";
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle("menu-board-flashy", flashy);
    document.documentElement.style.background = flashy ? "#0a0612" : "#fdf2fb";
    return () => document.body.classList.remove("menu-board-flashy");
  }, [flashy]);

  // Flashy "attract mode": after 60s with no interaction the menu fades out and
  // just the animated background keeps playing. Any interaction with the board
  // device — or the POS "Show Menu" button (via the /wake signal) — fades it back
  // in. Classic theme is always fully visible.
  const [menuHidden, setMenuHidden] = useState(false);
  const [previewFF, setPreviewFF] = useState(null);
  const [liveOrder, setLiveOrder] = useState(null);
  const lastWakeRef = useRef(Date.now());
  const hideOverrideRef = useRef(false);
  const lastServerWakeRef = useRef(0);
  const lastServerHideRef = useRef(0);
  const idleRaw = data?.layout?.idle_seconds;
  const neverFade = idleRaw === 0;
  const idleMs = Math.max(10, idleRaw || 60) * 1000;

  useEffect(() => {
    if (editMode) { setMenuHidden(false); return undefined; }
    if (!flashy) { setMenuHidden(false); return undefined; }
    const bump = () => { lastWakeRef.current = Date.now(); hideOverrideRef.current = false; setMenuHidden(false); };
    const hideNow = () => { hideOverrideRef.current = true; setMenuHidden(true); };
    const events = ["mousemove", "pointerdown", "keydown", "touchstart", "wheel"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));

    const tick = setInterval(() => {
      // neverFade: only an explicit POS "Hide" (hideOverrideRef) can hide it.
      setMenuHidden(hideOverrideRef.current || (!neverFade && Date.now() - lastWakeRef.current >= idleMs));
    }, 1000);

    const poll = setInterval(async () => {
      try {
        const { data: w } = await api.get("/menu-board/wake");
        const wake = w?.wake_at ? Date.parse(w.wake_at) : 0;
        const hide = w?.hide_at ? Date.parse(w.hide_at) : 0;
        // Whichever signal is newer wins.
        if (hide && hide > lastServerHideRef.current && hide >= wake) {
          lastServerHideRef.current = hide;
          hideNow();
        }
        if (wake && wake > lastServerWakeRef.current && wake > hide) {
          lastServerWakeRef.current = wake;
          bump();
        }
      } catch {
        /* ignore transient poll errors */
      }
    }, 4000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      clearInterval(tick);
      clearInterval(poll);
    };
  }, [flashy, idleMs, editMode, neverFade]);

  // Live editor bridge: when the admin free-form editor loads this board in an
  // iframe (?edit=1), it postMessages in-progress block rects so the operator
  // sees real content move in real time. We also ping the parent when ready.
  useEffect(() => {
    if (!editMode) return undefined;
    const onMsg = (e) => {
      const m = e.data;
      if (m && m.type === "bp-board-freeform") setPreviewFF(m.payload || {});
    };
    window.addEventListener("message", onMsg);
    try { window.parent?.postMessage({ type: "bp-board-ready" }, "*"); } catch { /* no-op */ }
    return () => window.removeEventListener("message", onMsg);
  }, [editMode]);

  // Poll the live order being rung up (customer-facing "Your Order" block).
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/menu-board/current-order");
        if (alive) setLiveOrder(data);
      } catch { /* ignore transient poll errors */ }
    };
    load();
    const id = setInterval(load, 3000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const groups      = useMemo(() => data?.modifier_groups || [], [data?.modifier_groups]);
  const sizeGroup   = useMemo(() => findGroup(groups, /^size$/i), [groups]);
  const baseGroup   = useMemo(() => findGroup(groups, /^base$|tea\s*type|liquid|milk\s*tea\s*base/i), [groups]);
  const flavorGroup = useMemo(() => findGroup(groups, /^flavor$|powder|syrup/i), [groups]);
  const pearlGroup  = useMemo(() => {
    const pearl = groups.find((g) => /pearl/i.test(g.name || ""));
    if (pearl) return pearl;
    return findGroup(groups, /topping|jelly|boba/i);
  }, [groups]);

  const itemsByCategoryName = useMemo(() => {
    const out = {};
    const cats = data?.categories || [];
    const items = data?.items || [];
    for (const cat of cats) {
      const key = (cat.name || "").trim().toLowerCase();
      if (!key) continue;
      out[key] = items
        .filter((i) => i.category_id === cat.id)
        .sort((a, b) => (a.sort || 0) - (b.sort || 0) || a.name.localeCompare(b.name));
    }
    return out;
  }, [data?.categories, data?.items]);

  const sections = data?.sections || {};
  const bobaItems   = itemsByCategoryName.boba   || [];
  const useBobaSection = sections.boba && bobaItems.length > 0;

  // Drag-and-drop layout (from the Admin → Menu Board editor).
  const layout = data?.layout || {};
  const panelOrder = layout.panel_order || ["step1", "step2", "step3", "step4"];
  const panelHidden = useMemo(() => new Set(layout.panel_hidden || []), [layout.panel_hidden]);
  const sidebarOrder = layout.sidebar_order || ["pearl_timer", "featured", "socials"];

  const stepCfg = useMemo(() => layout.steps || {}, [layout.steps]);
  const stepEnabled = (k) => stepCfg[k]?.enabled !== false;
  const groupById = (id) => (id ? groups.find((g) => g.id === id) : null);

  const renderPanel = (key, num) => {
    if (key.startsWith("step") && !stepEnabled(key)) return null;
    const cfg = stepCfg[key] || {};
    const picked = groupById(cfg.group_id);
    const t = (cfg.title || "").trim();
    switch (key) {
      case "step1":
        if (picked) {
          return <StepBlock number={num} title={t || picked.name} group={picked} accent="peach" hint={picked.board_hint || cfg.hint || ""} />;
        }
        return useBobaSection
          ? <CategoryBlock number={num} title={t || "Boba"} items={bobaItems} accent="peach" />
          : <StepBlock number={num} title={t || "Size"} group={sizeGroup} accent="peach" hint={sizeGroup?.board_hint || "Pick your cup."} />;
      case "step2":
        return <StepBlock number={num} title={t || (picked ? picked.name : "Base")} group={picked || baseGroup} accent="violet" hint={(picked || baseGroup)?.board_hint || "The liquid foundation — tea, milk, lemonade."} />;
      case "step3":
        return <StepBlock number={num} title={t || (picked ? picked.name : "Flavor")} group={picked || flavorGroup} accent="pink" twoCols hint={(picked || flavorGroup)?.board_hint || "Powder mixed into your base. Reg: up to 2 · Lg: up to 3."} rule={picked ? undefined : "Reg · 2 max  ·  Lg · 3 max"} />;
      case "step4":
        return <StepBlock number={num} title={t || (picked || pearlGroup)?.name || "Pearls & toppings"} group={picked || pearlGroup} accent="plum" twoCols hint={(picked || pearlGroup)?.board_hint || "Add as many as you like."} />;
      default:
        return null;
    }
  };
  const visiblePanels = panelOrder.filter((k) => !panelHidden.has(k) && stepEnabled(k));

  // Item-category menus (Milk Teas, Fruit Teas, …) — data-driven from the
  // owner's real categories, ordered/hidden via the Menu Board editor.
  const categories = useMemo(() => data?.categories || [], [data?.categories]);
  const itemsByCatId = useMemo(() => {
    const out = {};
    for (const it of (data?.items || [])) {
      (out[it.category_id] = out[it.category_id] || []).push(it);
    }
    for (const id of Object.keys(out)) {
      out[id].sort((a, b) => (a.sort || 0) - (b.sort || 0) || a.name.localeCompare(b.name));
    }
    return out;
  }, [data?.items]);
  const catAccents = ["teal", "peach", "pink", "violet", "plum"];
  // The "Boba" item-category is already surfaced as build-step 1 when the
  // boba section is on — don't also list it in the generic category row.
  const bobaCatId = useMemo(
    () => (categories.find((c) => (c.name || "").toLowerCase() === "boba") || {}).id,
    [categories],
  );
  const visibleCategories = useMemo(() => {
    const order = layout.category_order || categories.map((c) => c.id);
    const hidden = new Set(layout.category_hidden || []);
    return order
      .map((id) => categories.find((c) => c.id === id))
      .filter(Boolean)
      .filter((c) => !hidden.has(c.id) && (itemsByCatId[c.id] || []).length > 0)
      .filter((c) => !(useBobaSection && c.id === bobaCatId));
  }, [layout.category_order, layout.category_hidden, categories, itemsByCatId, useBobaSection, bobaCatId]);

  const sidebarEls = {
    pearl_timer: data?.pearl_timer ? <PearlTimerRibbon timer={data.pearl_timer} compact /> : null,
    featured: data?.featured_drink ? <FeaturedDrinkHero featured={data.featured_drink} compact /> : null,
    socials: data?.social_media ? <SocialFollowBar social={data.social_media} vertical /> : null,
  };

  if (isError) {
    return (
      <div className="min-h-screen grid place-items-center text-primary p-12 bg-white">
        <div className="text-center">
          <img src={LOGO_URL} alt="" className="w-24 h-24 mx-auto mb-4 opacity-60" />
          <h1 className="font-display text-5xl font-bold mb-2">Menu unavailable</h1>
          <p className="opacity-60 text-lg">Trying again in a moment…</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen grid place-items-center bg-white">
        <div className="text-center">
          <img src={LOGO_URL} alt="" className="w-20 h-20 mx-auto bp-dance" />
          <p className="font-display text-2xl text-primary/70 animate-pulse mt-4">
            Loading the menu…
          </p>
        </div>
      </div>
    );
  }

  const featured = data.featured_drink;
  const hasSidebar = Boolean(featured || data.pearl_timer || data.social_media);

  // ---- Free-form (drag-anywhere) layout support ----
  const ffAll = previewFF || data?.layout?.freeform || {};
  const ffStyle = ffAll?.[style] || {};
  const freeformOn = Boolean(ffStyle.enabled);
  const ffBlocks = ffStyle.blocks || {};

  const headerNode = (
    <header className="flex items-center justify-between gap-6 h-full" data-ff="header">
      <div className="flex items-center gap-5">
        <img
          src={LOGO_URL}
          alt={data.store?.name || "The Boba Place"}
          className="w-24 h-24 lg:w-28 lg:h-28 object-contain bp-bob select-none drop-shadow-[0_10px_20px_hsla(252,63%,54%,0.35)]"
          draggable="false"
          data-testid="menu-board-logo"
        />
        <div>
          {data.store?.tagline_enabled !== false && (data.store?.tagline || "").trim() && (
            <span
              className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-white/85 border-2 border-white text-xs font-black text-primary uppercase tracking-[0.2em] shadow-[0_6px_16px_hsla(252,63%,54%,0.25)]"
              data-testid="menu-board-tagline"
            >
              <Sparkles className="w-3.5 h-3.5 text-accent" />
              {data.store.tagline}
            </span>
          )}
          <h1 className="font-display font-black text-6xl lg:text-7xl tracking-tight mt-1 leading-[0.92] drop-shadow-[0_3px_0_rgba(255,255,255,0.7)]">
            <span className="bg-gradient-to-r from-violet-600 via-pink-500 to-amber-400 bg-clip-text text-transparent">
              {data.store?.name || "The Boba Place"}
            </span>
          </h1>
          <p className="font-display text-xl lg:text-2xl font-bold text-foreground/80 mt-1">
            Build your boba in 4 steps — or grab today's special!
          </p>
        </div>
      </div>
      <div className="text-right shrink-0 flex items-center gap-4">
        <div className="bp-candy-glass px-5 py-3 rounded-[28px]">
          <p className="text-muted-foreground text-xs uppercase tracking-widest font-black">Hours</p>
          <p className="font-display text-2xl font-black text-foreground">{data.store?.hours}</p>
          {data.store?.contact_phone && (
            <p className="font-display text-lg text-primary font-bold">{data.store.contact_phone}</p>
          )}
        </div>
        <KawaiiBobaCup variant="matcha" className="w-24 h-32 bp-dance bp-mascot shrink-0" />
      </div>
    </header>
  );

  const categoriesNode = visibleCategories.length > 0 ? (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="menu-board-extras-row">
      {visibleCategories.map((c, i) => (
        <CategoryBlock
          key={c.id}
          title={c.name}
          items={itemsByCatId[c.id] || []}
          accent={catAccents[i % catAccents.length]}
        />
      ))}
    </div>
  ) : null;

  const footerNode = (
    <footer className="relative flex items-center justify-between border-t-2 border-primary/10 pt-3 text-muted-foreground text-base font-semibold h-full" data-testid="footer-mascots">
      <div className="flex items-center gap-3">
        <KawaiiBobaCup variant="taro" className="w-14 h-20 bp-dance bp-mascot" />
        <KawaiiBobaCup variant="mango" className="w-14 h-20 bp-dance bp-mascot" flip style={{ animationDelay: "0.4s" }} />
        <p className="ml-2 text-foreground/70">
          <span className="font-display font-black text-foreground">$1</span> per extra topping ·
          Loyalty points on every order ·
          Ask staff about today's seasonal flavor!
        </p>
      </div>
      <p className="font-mono text-sm opacity-60" suppressHydrationWarning data-testid="last-updated-timestamp">
        Updated {data.fetched_at?.slice(11, 19) || "—"} UTC
      </p>
    </footer>
  );

  const blockNodes = {
    header: headerNode,
    step1: renderPanel("step1", 1),
    step2: renderPanel("step2", 2),
    step3: renderPanel("step3", 3),
    step4: renderPanel("step4", 4),
    pearl_timer: sidebarEls.pearl_timer,
    featured: sidebarEls.featured,
    socials: sidebarEls.socials,
    categories: categoriesNode,
    footer: footerNode,
    order: <LiveOrderTicket order={liveOrder} />,
  };

  const freeformLayer = (
    <div className="absolute inset-0" data-testid="menu-board-freeform">
      {Object.keys(blockNodes).map((key) => {
        const node = blockNodes[key];
        const rect = ffBlocks[key];
        if (!node || !rect || rect.hidden) return null;
        const boxW = (rect.w / 100) * 1920;
        const boxH = (rect.h / 100) * 1080;
        return (
          <div
            key={key}
            className="absolute"
            style={{ left: `${rect.x}%`, top: `${rect.y}%`, width: `${rect.w}%`, height: `${rect.h}%` }}
            data-ff-block={key}
          >
            <FitToBox boxW={boxW} boxH={boxH} ha={rect.ha} va={rect.va}>{node}</FitToBox>
          </div>
        );
      })}
    </div>
  );

  return (
    <div
      data-testid="menu-board-viewport"
      className="fixed inset-0 overflow-hidden"
      style={{ background: flashy ? "#0a0612" : "#fdf2fb" }}
    >
    {flashy && <FlashyVideoBg />}
    <div
      ref={boardRef}
      data-testid="page-menu-board"
      className="bp-noise text-foreground overflow-hidden"
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        width: "1920px",
        minHeight: "1080px",
        transform: `translate(-50%, -50%) scale(${fit.sx}, ${fit.sy})`,
        transformOrigin: "center center",
        background: flashy
          ? "transparent"
          : "linear-gradient(160deg, hsl(320 100% 97%) 0%, hsl(255 96% 97%) 45%, hsl(190 96% 96%) 100%)",
      }}
    >
      {/* Animated aurora orbs + drifting confetti behind everything */}
      {!flashy && <AuroraBackdrop />}
      <KawaiiConfetti />

      {/* Faint pearl-pattern overlay (classic only) */}
      {!flashy && (
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.12]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 14px 14px, rgba(91,63,209,0.2) 1.5px, transparent 2px)",
          backgroundSize: "60px 60px",
        }}
      />
      )}
      <main
        className={
          freeformOn
            ? "relative z-10 w-full h-[1080px]"
            : "relative z-10 w-full px-10 py-4 flex flex-col gap-4 min-h-[1080px]"
        }
        style={{
          transition: "opacity 1200ms ease",
          opacity: menuHidden ? 0 : 1,
          pointerEvents: menuHidden ? "none" : "auto",
        }}
        data-testid="menu-board-main"
      >
        {freeformOn ? freeformLayer : (
        <>
        {/* Header */}
        {headerNode}

        {/* Body — 4-step menu on the left, featured/pearl/socials in a right sidebar. */}
        <div
          className={
            hasSidebar
              ? "flex-1 grid grid-cols-[minmax(0,1fr)_400px] gap-6"
              : "flex-1 grid grid-cols-1"
          }
        >
          <div className="grid grid-cols-2 gap-5 items-start self-center w-full">
            {visiblePanels.map((k, i) => (
              <Fragment key={k}>{renderPanel(k, i + 1)}</Fragment>
            ))}
          </div>

          {hasSidebar && (
            <aside className="flex flex-col gap-5 h-full min-h-0" data-testid="menu-board-sidebar">
              {sidebarOrder.map((k) => (sidebarEls[k] ? <Fragment key={k}>{sidebarEls[k]}</Fragment> : null))}
            </aside>
          )}
        </div>

        {/* Item-category menus (Milk Teas, Fruit Teas, …) — driven by the
            owner's real categories + the Menu Board editor order/visibility. */}
        {categoriesNode && <div className="shrink-0">{categoriesNode}</div>}

        {/* Footer with extra dancing kawaii cups */}
        {footerNode}
        </>
        )}
      </main>
    </div>
    </div>
  );
}
